import { useEffect, useState } from 'react';
import { evaluateEligibility, getTripRoute } from '../dispatch.api';
import type { EligibilityResponse } from '../policies/picker-candidates.policy';
import type { SuggestionWindow } from '../policies/suggestion-window.policy';

export type EligibilityState = { status: 'loading' } | { status: 'loaded'; response: EligibilityResponse } | { status: 'error' };

interface Answer {
  tripId: string;
  window: SuggestionWindow;
  state: Exclude<EligibilityState, { status: 'loading' }>;
}

/**
 * The whole fleet's verdicts for the trip, once per trip and window (not per keystroke or page). Its failure is its own state: the ranked
 * crews do not depend on it, so the page keeps showing them and the Blocked section says it could not load. An answer belongs to the trip and
 * window it was asked for, so after either changes the previous trip's rows are not shown while the new request is out.
 */
export function useBlockedCandidates(tripId: string, window: SuggestionWindow): EligibilityState {
  const [answer, setAnswer] = useState<Answer | null>(null);

  useEffect(() => {
    let cancelled = false;
    const settle = (state: Answer['state']) => {
      if (!cancelled) setAnswer({ tripId, window, state });
    };

    getTripRoute(tripId)
      .then(({ jobId, originZoneCode, destinationZoneCode }) => {
        if (!originZoneCode || !destinationZoneCode) throw new Error('The trip has no route to judge against');
        return evaluateEligibility({ jobId, originZoneCode, destinationZoneCode }, window);
      })
      .then((response) => settle({ status: 'loaded', response }))
      .catch(() => settle({ status: 'error' }));

    return () => {
      cancelled = true;
    };
  }, [tripId, window]);

  return answer?.tripId === tripId && answer.window === window ? answer.state : { status: 'loading' };
}
